import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

export interface DirectDocumentBudgetRetryBannerProps {
    onRetry: () => void;
    /** True while the refetch is in flight. */
    isRetrying?: boolean;
    className?: string;
}

const FAILURE_COPY =
    "Couldn't check your document budget. Spreadsheets can still be attached.";

/**
 * Settled-failure inline retry for the budget query.
 *
 * The failure text carries `role="alert"` — it follows an action that did not
 * complete, and a sighted user sees it appear while a screen-reader user would
 * not. The retry transition is `aria-live="polite"` instead: it is progress,
 * not a failure, and should not interrupt what is being read.
 */
export function DirectDocumentBudgetRetryBanner({
    onRetry,
    isRetrying = false,
    className,
}: DirectDocumentBudgetRetryBannerProps) {
    return (
        <div
            data-slot="direct-document-budget-retry"
            className={cn(
                "border-border bg-muted/40 flex flex-wrap items-center gap-2 rounded-md border px-3 py-2",
                className
            )}
        >
            <p
                role="alert"
                className="text-muted-foreground flex-1 text-xs leading-[1.55]"
            >
                {FAILURE_COPY}
            </p>
            <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={onRetry}
                disabled={isRetrying}
            >
                {isRetrying ? "Retrying…" : "Retry"}
            </Button>
            <span aria-live="polite" className="sr-only">
                {isRetrying ? "Retrying…" : ""}
            </span>
        </div>
    );
}
